import { useEffect, useState } from "react";
import { ImagePlus } from "lucide-react";
import type { ContentLibrary } from "../api/contract";
import { emptyField, itemDraft, itemHasLinkAndPhoto, itemPatch, type ItemDraft } from "../lib/itemFields";
import { FIELDS } from "./ContentView";
import { Spinner } from "./Spinner";

type Entry = ContentLibrary["entries"][number];

/** Fields that hold a sentence or more get a taller box. */
const LONG = new Set(["description", "text", "answer", "body"]);

interface ItemFieldsProps {
  entry: Entry;
  /** The owner's photos, for the item's photo. */
  photos: Array<{ id: string; label: string; src: string }>;
  /** Saves what changed; resolves false when it wasn't saved. */
  onSave: (set: Record<string, string | null>) => Promise<boolean>;
}

/**
 * A list item's own fields, where the owner selected it: its photo, words and link, changed exactly
 * and saved straight away. The note to Stax below it is for everything else.
 */
export function ItemFields({ entry, photos, onSave }: ItemFieldsProps) {
  const [draft, setDraft] = useState<ItemDraft>(() => itemDraft(entry));
  const [choosing, setChoosing] = useState(false);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  // A different item, or the same one saved and reloaded, starts from what it now says.
  useEffect(() => {
    setDraft(itemDraft(entry));
    setChoosing(false);
  }, [entry]);

  const patch = itemPatch(entry, draft);
  const empty = emptyField(draft);
  const photo = draft.image ? photos.find((candidate) => candidate.id === draft.image) : null;
  const edit = (name: string, value: string) => {
    setDraft((now) => ({ ...now, fields: { ...now.fields, [name]: value } }));
    setState("idle");
  };
  // Not a form of its own: it sits inside the note card's form.
  const save = async () => {
    if (!patch || empty || state === "saving") return;
    setState("saving");
    setState(await onSave(patch) ? "saved" : "failed");
  };

  return (
    <div className="bw-item-fields" role="group" aria-label={`Change ${entry.label}`}>
      {itemHasLinkAndPhoto(entry) && (
        <div className="bw-fields-photo">
          {photo ? <img src={photo.src} alt="" /> : <span className="bw-fields-photo-empty" aria-hidden="true"><ImagePlus size={18} /></span>}
          <span>Photo</span>
          <button type="button" className="bw-btn bw-btn--text" aria-label={photo ? "Change photo" : "Add photo"} aria-expanded={choosing} onClick={() => setChoosing((now) => !now)}>{photo ? "Change" : "Add"}</button>
          {photo && <button type="button" className="bw-btn bw-btn--text" aria-label="Remove photo" onClick={() => { setDraft((now) => ({ ...now, image: null })); setState("idle"); }}>Remove</button>}
        </div>
      )}
      {choosing && (
        <div className="bw-fields-photo-grid" role="group" aria-label="Your photos">
          {photos.map((candidate) => (
            <button key={candidate.id} type="button" aria-pressed={draft.image === candidate.id} aria-label={candidate.label} onClick={() => { setDraft((now) => ({ ...now, image: candidate.id })); setChoosing(false); setState("idle"); }}>
              <img src={candidate.src} alt="" />
            </button>
          ))}
          {photos.length === 0 && <p className="bw-item-note">No photos yet. Tap the photo on the page to upload one.</p>}
        </div>
      )}
      {Object.entries(draft.fields).map(([name, value]) => {
        const id = `bw-item-${entry.id}-${name}`;
        const label = name === "url" ? "Link" : FIELDS[name] ?? name;
        return (
          <div key={name} className="bw-item-field">
            <label htmlFor={id}>{label}</label>
            {LONG.has(name)
              ? <textarea id={id} rows={2} value={value} onChange={(event) => edit(name, event.target.value)} />
              : <input id={id} type={name === "url" ? "url" : "text"} inputMode={name === "url" ? "url" : undefined} autoCapitalize={name === "url" ? "none" : undefined} placeholder={name === "url" ? "https://…" : undefined} value={value} onChange={(event) => edit(name, event.target.value)} />}
          </div>
        );
      })}
      <div className="bw-item-save">
        <span className="bw-item-note" role={state === "failed" || empty ? "alert" : "status"}>
          {empty ? `${FIELDS[empty] ?? "This"} can’t be empty.` : state === "saved" ? "Saved. You can undo it in Changes." : state === "failed" ? "That didn’t save. Try again." : "Saves straight away."}
        </span>
        <button type="button" className="bw-btn bw-btn--primary" onClick={() => void save()} disabled={!patch || Boolean(empty) || state === "saving"}>
          {state === "saving" && <Spinner />}{state === "saving" ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
